import { useTranslation } from "react-i18next";
import { Accordion, AccordionDetails, Box, Link, Stack, Typography } from "~/common/mui";
import { SCHAEFFLER_LOGO } from "~/utils/url/staticResources";
import { LanguageSelector } from "~/features/categoryNavigation/components/LanguageSelector";
import { AccordionSummary } from "@mui/material";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { SocialLinks } from "./SocialLinks";
import MarqueeText from "./MarqueeText";
import { sendGAEvent } from "~/app/analytics/ga/ga";
import {
  FooterBottom,
  FooterHeader,
  FooterLink,
  FooterNavigation,
  FooterSection,
  FooterTopLinkItem,
  LinksColumn,
  LogoWrapper,
} from "./footerStyles";

interface Props extends PageFooterProps {
  className?: string;
}

const FooterTopLinks = ({ links }: { links: FooterNavigationNode[] }) => {
  const { t } = useTranslation();
  if (!links?.length) {
    return null;
  }

  return (
    <FooterTopLinkItem>
      {links.map((link) => (
        <li key={link.title}>
          {link.url ? (
            <Link
              variant="titleLarge"
              href={link.url}
              id={link.gaLabel}
              data-ga-name={link.gaLabel}
              onClick={handleFooterClick}
              sx={{ color: "textPrimary.main" }}
            >
              {t(link.title)}
            </Link>
          ) : (
            <Typography variant="titleLarge" sx={{ color: "textPrimary.main" }}>
              {t(link.title)}
            </Typography>
          )}
        </li>
      ))}
    </FooterTopLinkItem>
  );
};

const FooterLinks = ({ links }: { links: FooterNavigationNode[] }) => {
  const { t } = useTranslation();
  if (!links?.length) {
    return null;
  }

  return (
    <LinksColumn>
      {links.map((link) => (
        <li key={link.title}>
          {link.url ? (
            <FooterLink
              href={link.url}
              id={link.gaLabel}
              data-ga-name={link.gaLabel}
              onClick={handleFooterClick}
              sx={{ color: "textSecondary.main" }}
            >
              {t(link.title)}
            </FooterLink>
          ) : (
            <Typography sx={{ color: "textSecondary.main" }}>{t(link.title)}</Typography>
          )}
        </li>
      ))}
    </LinksColumn>
  );
};

const handleFooterClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
  sendGAEvent({
    eventCategory: "Navigation",
    eventAction: "Footer",
    eventLabel: event.currentTarget.dataset.gaName,
  });
};

export const PageFooter = ({ navigation, topLinks, social, footerCopyright }: Props) => {
  const { t } = useTranslation();
  const size = useNumericScreenSize();
  const isMobile = size < useNumericScreenSize.tablet;

  return (
    <Box component="footer" sx={{ width: "100%", overflow: "hidden" }}>
      <MarqueeText text={t("footer.marqee.text")} />
      <Box className="container" sx={{ p: 0 }}>
        <FooterHeader>
          <Box>{topLinks?.length > 0 && <FooterTopLinks links={topLinks} />}</Box>
          <LogoWrapper>
            <Box component="img" sx={{ height: "22px" }} src={SCHAEFFLER_LOGO} alt={t("schaeffler.logo.alt.text")} />
          </LogoWrapper>
        </FooterHeader>
      </Box>

      <FooterSection>
        <Box className="container" sx={{ p: 0 }}>
          <FooterNavigation>
            {navigation?.map((column) =>
              isMobile ? (
                <Accordion key={column.title}>
                  <AccordionSummary>
                    <Typography sx={{ color: "textPrimary.main" }}>{column.title}</Typography>
                  </AccordionSummary>

                  <AccordionDetails>
                    <FooterLinks links={column.children} />
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Box key={column.title}>
                  <Typography variant="bodyLarge" sx={{ color: "textPrimary.main", mb: 3 }}>
                    {column.title}
                  </Typography>

                  <FooterLinks links={column.children} />
                </Box>
              ),
            )}
          </FooterNavigation>
        </Box>
      </FooterSection>

      <FooterSection>
        <Box className="container" sx={{ p: 0 }}>
          <FooterBottom isMobile={isMobile}>
            <Typography sx={{ color: "textSecondary.main" }}>{footerCopyright}</Typography>
            <Stack sx={{ alignItems: "center", flexDirection: isMobile ? "column" : "row", gap: 3 }}>
              <LanguageSelector />
              {social?.length ? <SocialLinks social={social} /> : null}
            </Stack>
          </FooterBottom>
        </Box>
      </FooterSection>
    </Box>
  );
};

export interface PageFooterProps {
  navigation: FooterNavigationNode[];
  topLinks: FooterNavigationNode[];
  social: FooterNavigationNode[];
  footerCopyright?: string;
}

export interface FooterNavigationNode {
  title: string;
  gaLabel: string;
  url?: string;
  children: FooterNavigationNode[];
}

export const createPageFooter = (props: PageFooterProps) => {
  return <PageFooter {...props} />;
};
